/* eslint-disable complexity */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { ButtonPrimary, ButtonSecondary, ButtonTertiary } from '@americanexpress/dls-react';
import PropTypes from 'prop-types';
import {
  flexDirectionType,
  justifyContentType,
  mapflexDirectionTypeToClassName,
  mapJustifyContentTypeToClassName,
} from '../../constants/styles';

/* Added functionality to be able to display buttons with optional icons */
export const FxMultiButtons = React.memo((props) => {
  // Added functionality to use FxMultiButtons from Preview page schemas
  // by passing values to component property
  // if component property is passed, use it, else use props
  const { component: componentProps } = props;
  const {
    id,
    primaryButtonTheme = {},
    primaryButtonLabel,
    onPrimaryButtonClick,
    isDisablePrimaryButton,
    primaryBtnIcon,
    secondaryButtonTheme = {},
    secondaryButtonLabel,
    onSecondaryButtonClick,
    isDisableSecondaryBtn,
    secondaryBtnClass,
    secondaryBtnIcon,
    tertiaryButtonTheme = {},
    tertiaryButtonLabel,
    onTertiaryButtonClick,
    isDisableTertiaryBtn,
    tertiaryBtnIcon,
    justifyButtons,
    flexDirection,
    parentPrimaryButton,
  } = componentProps || props;

  const {
    filled: primBtnIconFilled,
    size: primBtnIconSize = 'sm',
  } = primaryBtnIcon || {};
  const {
    filled: secBtnIconFilled,
    size: secBtnIconSize = 'sm',
  } = secondaryBtnIcon || {};
  const {
    filled: tertBtnIconFilled,
    size: tertBtnIconSize = 'sm',
  } = tertiaryBtnIcon || {};

  const primaryButton = !parentPrimaryButton && primaryButtonLabel && (
    <ButtonPrimary
      id={`${id}-ButtonPrimary`}
      onClick={onPrimaryButtonClick}
      type={onPrimaryButtonClick ? 'button' : 'submit'}
      disabled={isDisablePrimaryButton}
      icon={primaryBtnIcon
        ? (
          <primaryBtnIcon.icon
            id={`${id}-Icon-ButtonPrimary`}
            filled={primBtnIconFilled}
            size={primBtnIconSize}
          />
        ) : null}
      theme={primaryButtonTheme}
    >
      {primaryButtonLabel}
    </ButtonPrimary>
  );

  const secondaryButton = secondaryButtonLabel && (
    <ButtonSecondary
      id={`${id}-ButtonSecondary`}
      className={secondaryBtnClass}
      onClick={onSecondaryButtonClick}
      type={onSecondaryButtonClick ? 'button' : 'submit'}
      disabled={isDisableSecondaryBtn}
      icon={secondaryBtnIcon
        ? (
          <secondaryBtnIcon.icon
            id={`${id}-Icon-ButtonSecondary`}
            filled={secBtnIconFilled}
            size={secBtnIconSize}
          />
        ) : null}
      theme={secondaryButtonTheme}
    >
      {secondaryButtonLabel}
    </ButtonSecondary>
  );

  const tertiaryButton = tertiaryButtonLabel && onTertiaryButtonClick && (
    <ButtonTertiary
      id={`${id}-ButtonTertiary`}
      className="margin-responsive-r"
      onClick={onTertiaryButtonClick}
      disabled={isDisableTertiaryBtn}
      icon={tertiaryBtnIcon
        ? (
          <tertiaryBtnIcon.icon
            id={`${id}-Icon-ButtonTertiary`}
            filled={tertBtnIconFilled}
            size={tertBtnIconSize}
          />
        ) : null}
      theme={tertiaryButtonTheme}
    >
      {tertiaryButtonLabel}
    </ButtonTertiary>
  );

  return (
    <div
      className={`flex ${mapJustifyContentTypeToClassName[justifyButtons]} ${mapflexDirectionTypeToClassName[flexDirection]}`}
    >
      {tertiaryButton}
      {secondaryButton}
      {parentPrimaryButton || primaryButton}
    </div>
  );
});

FxMultiButtons.displayName = 'FxMultiButtons';

FxMultiButtons.propTypes = {
  primaryButtonTheme: PropTypes.shape({}),
  primaryButtonLabel: PropTypes.string,
  onPrimaryButtonClick: PropTypes.func,
  isDisablePrimaryButton: PropTypes.bool,
  primaryBtnIcon: PropTypes.shape({
    icon: PropTypes.func,
    filled: PropTypes.bool,
    size: PropTypes.string,
  }),
  secondaryButtonTheme: PropTypes.shape({}),
  secondaryButtonLabel: PropTypes.string,
  onSecondaryButtonClick: PropTypes.func,
  isDisableSecondaryBtn: PropTypes.bool,
  secondaryBtnClass: PropTypes.string,
  secondaryBtnIcon: PropTypes.shape({
    icon: PropTypes.func,
    filled: PropTypes.bool,
    size: PropTypes.string,
  }),
  tertiaryButtonTheme: PropTypes.shape({}),
  tertiaryButtonLabel: PropTypes.func,
  onTertiaryButtonClick: PropTypes.func,
  isDisableTertiaryBtn: PropTypes.bool,
  tertiaryBtnIcon: PropTypes.shape({
    icon: PropTypes.func,
    filled: PropTypes.bool,
    size: PropTypes.string,
  }),
  justifyButtons: PropTypes.oneOf(Object.values(justifyContentType)),
  flexDirection: PropTypes.oneOf(Object.values(flexDirectionType)),
  parentPrimaryButton: PropTypes.element,
};

FxMultiButtons.defaultProps = {
  id: 'FxMultiButtons',
  primaryButtonTheme: {},
  primaryButtonLabel: '',
  onPrimaryButtonClick: null,
  isDisablePrimaryButton: false,
  primaryBtnIcon: null,
  secondaryButtonTheme: {},
  secondaryButtonLabel: '',
  onSecondaryButtonClick: null,
  isDisableSecondaryBtn: false,
  secondaryBtnClass: 'margin-responsive-r',
  secondaryBtnIcon: null,
  tertiaryButtonTheme: {},
  tertiaryButtonLabel: '',
  onTertiaryButtonClick: null,
  isDisableTertiaryBtn: false,
  tertiaryBtnIcon: null,
  justifyButtons: justifyContentType.END,
  flexDirection: flexDirectionType.ROW,
  parentPrimaryButton: null,
};
